import { useMemo } from 'react';
import styled from 'styled-components';

import { ReceptionServiceWithPractitioners } from '@dreryk/egabinet-private-medical-service-frontend-api';
import { ReceptionServiceId } from '@dreryk/egabinet-private-medical-service-published-language-api';
import { ServiceLocation } from '@dreryk/egabinet-healthcare-provider-frontend-api';

import { FormContextValues, useFormController } from '@dreryk/react-common';
import {
   FormController,
   Notification,
   NotificationSize,
   NotificationType,
   StyledFlex,
   StyledMargin,
   StyledMaxWidth,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { receptionServiceTranslationKeys } from '../../../translations';
import {
   getEstimatedDurationValue,
   mapReceptionServicePractitionersToBean,
} from '../../shared/form/components/practitioner/receptionServicePractitioner.utils';
import {
   mapReceptionServicePriceToBean,
   receptionServicePriceVisitor,
} from '../../shared/form/components/price/receptionServicePrice.utils';
import { ReceptionServiceUseDefaultConfigurationCheckbox } from '../../shared/form/components/price/ReceptionServiceUseDefaultConfigurationCheckbox';
import { ReceptionServiceForm, ReceptionServiceFormBean } from '../../shared/form/ReceptionServiceForm';
import {
   mapReceptionServiceLocationsToBean,
   serviceLocationControllerVisitor,
} from '../../shared/form/components/serviceLocations/serviceLocations.utils';

const StyledForColumnsSpan = styled.div`
   grid-column: 1 / span 4;
`;

StyledForColumnsSpan.displayName = 'StyledTwoColumnsSpan';

const StyledForm = styled.form`
   display: grid;
   grid-template-columns: repeat(4, 1fr);
   column-gap: 36px;
   row-gap: 24px;
`;

StyledForm.displayName = 'StyledForm';

export type PractitionerReceptionServiceUpdateFormBean = ReceptionServiceFormBean & {
   id: ReceptionServiceId;
   usingDefaultConfiguration: boolean;
};

export type PractitionerReceptionServiceUpdateFormController = FormContextValues<PractitionerReceptionServiceUpdateFormBean>;

declare type PractitionerReceptionServiceUpdateFormRenderProps = Pick<
   PractitionerReceptionServiceUpdateFormController,
   'handleSubmit' | 'formState'
>;

declare type PractitionerReceptionServiceUpdateFormRenderer = (
   renderProps: PractitionerReceptionServiceUpdateFormRenderProps
) => JSX.Element;

type ReceptionServiceUpdateFormProps = {
   readonly children: PractitionerReceptionServiceUpdateFormRenderer;
   readonly receptionService: ReceptionServiceWithPractitioners;
   readonly serviceLocations: ServiceLocation[];
};

// eslint-disable-next-line max-lines-per-function
export const PractitionerReceptionServiceUpdateForm = (props: ReceptionServiceUpdateFormProps): JSX.Element => {
   const controller = useFormController<PractitionerReceptionServiceUpdateFormBean>(
      useMemo(() => {
         const [practitionerService] = props.receptionService.practitioners;
         const [{ usingDefaultConfiguration, estimatedDuration }] = props.receptionService.practitioners;

         return {
            id: props.receptionService.id,
            name: props.receptionService.name,
            additionalInformation: props.receptionService.additionalInformation ?? null,
            color: props.receptionService.color?.asHexString() ?? null,
            usingDefaultConfiguration: usingDefaultConfiguration,
            videoConsultation: props.receptionService.deliveryMode === 'VIDEO_CONSULTATION',
            estimatedDuration: getEstimatedDurationValue(
               usingDefaultConfiguration,
               props.receptionService.estimatedDuration,
               estimatedDuration
            ),
            ...mapReceptionServicePriceToBean({
               maxPrice: practitionerService.maxPrice || props.receptionService.maxPrice,
               minPrice: practitionerService.minPrice || props.receptionService.minPrice,
            }),
            ...mapReceptionServicePractitionersToBean(props.receptionService, [practitionerService.practitioner]),
            ...mapReceptionServiceLocationsToBean(props.receptionService, props.serviceLocations),
         };
      }, [props.receptionService, props.serviceLocations])
   );

   const bean = controller.watch();

   return (
      <>
         <FormController controller={controller.register('id')} />
         <ReceptionServiceForm>
            <ReceptionServiceForm.FourColumnsSpan>
               <StyledFlex direction={'column'} gap={8}>
                  <ReceptionServiceForm.NameTextInput register={controller.register} disabled />
                  <Notification
                     type={NotificationType.INFO}
                     size={NotificationSize.SMALLER}
                     testId={'globalFieldsInformation'}
                     label={receptionServiceTranslationKeys.update.form.information.globalFields}
                  />
               </StyledFlex>
            </ReceptionServiceForm.FourColumnsSpan>

            {protectFeature(
               'managingVideoConsultations',
               <ReceptionServiceForm.FourColumnsSpan>
                  <ReceptionServiceForm.VideoConsultationCheckbox register={controller.register} disabled />
               </ReceptionServiceForm.FourColumnsSpan>
            )}

            <ReceptionServiceForm.FourColumnsSpan>
               <ReceptionServiceUseDefaultConfigurationCheckbox
                  register={controller.register}
                  setValue={controller.setValue}
                  receptionService={props.receptionService}
               />
            </ReceptionServiceForm.FourColumnsSpan>

            <ReceptionServiceForm.FourColumnsSpan>
               <StyledFlex justifyContent={'space-between'} alignItems={'center'}>
                  <StyledMaxWidth maxWidth={133}>
                     <ReceptionServiceForm.EstimatedDuration
                        disabled={bean.usingDefaultConfiguration}
                        register={controller.register}
                     />
                  </StyledMaxWidth>
                  <StyledMargin right={132}>
                     <ReceptionServiceForm.ColorSelectorGroup register={controller.register} disabled />
                  </StyledMargin>
               </StyledFlex>
            </ReceptionServiceForm.FourColumnsSpan>

            <ReceptionServiceForm.TwoColumnsSpan>
               <StyledFlex direction={'column'} gap={'8px'} flex={'1'}>
                  {receptionServicePriceVisitor(bean)({
                     asFree: () => (
                        <FormController controller={controller.register('freeChecked')}>
                           {() => <ReceptionServiceForm.FreeText />}
                        </FormController>
                     ),

                     withPrice: () => (
                        <StyledFlex direction={'row'} gap={'8px'}>
                           <StyledMaxWidth maxWidth={133}>
                              <ReceptionServiceForm.PriceMoneyInput
                                 register={controller.register}
                                 disabled={bean.usingDefaultConfiguration}
                              />
                           </StyledMaxWidth>
                           <StyledMargin top={36}>
                              <ReceptionServiceForm.PriceRangeCheckbox
                                 register={controller.register}
                                 disabled={bean.usingDefaultConfiguration}
                              />
                           </StyledMargin>
                        </StyledFlex>
                     ),

                     withPriceRange: () => (
                        <StyledFlex direction={'row'} gap={'8px'}>
                           <StyledMaxWidth maxWidth={302}>
                              <ReceptionServiceForm.PriceMoneyRangeInput
                                 register={controller.register}
                                 disabled={bean.usingDefaultConfiguration}
                              />
                           </StyledMaxWidth>
                           <StyledMargin top={36}>
                              <ReceptionServiceForm.PriceRangeCheckbox
                                 register={controller.register}
                                 disabled={bean.usingDefaultConfiguration}
                              />
                           </StyledMargin>
                        </StyledFlex>
                     ),
                  })}
               </StyledFlex>
            </ReceptionServiceForm.TwoColumnsSpan>

            <ReceptionServiceForm.FourColumnsSpan>
               <ReceptionServiceForm.AdditionalInformationTextarea register={controller.register} disabled />
            </ReceptionServiceForm.FourColumnsSpan>

            <ReceptionServiceForm.FourColumnsSpan>
               <ReceptionServiceForm.PractitionerTextView
                  register={controller.register}
                  defaultValue={bean.practitioner!}
               />
            </ReceptionServiceForm.FourColumnsSpan>

            <ReceptionServiceForm.FourColumnsSpan>
               {serviceLocationControllerVisitor(
                  controller,
                  props.serviceLocations
               )({
                  single: ({ controller, serviceLocation }) => (
                     <ReceptionServiceForm.ServiceLocationTextView
                        register={controller.register}
                        defaultValue={serviceLocation}
                     />
                  ),
                  multiple: ({ controller }) => (
                     <ReceptionServiceForm.ServiceLocationsInteractiveTable
                        register={controller.register}
                        header={
                           <ReceptionServiceForm.ServiceLocationsInteractiveTable.Selector
                              serviceLocations={props.serviceLocations}
                              register={controller.register}
                              control={controller.control}
                              setValue={controller.setValue}
                              readOnly
                           />
                        }
                        readOnly
                     />
                  ),
               })}
            </ReceptionServiceForm.FourColumnsSpan>
         </ReceptionServiceForm>

         {props.children({
            handleSubmit: controller.handleSubmit,
            formState: controller.formState,
         })}
      </>
   );
};
